import { CloudUploadIcon, FileXIcon } from 'lucide-react'
import { useCallback } from 'react'
import {
  type Accept,
  type DropEvent,
  type FileRejection,
  type FileWithPath,
  useDropzone,
} from 'react-dropzone'

import { Link } from '@/components'
import type { FileDropzoneItem } from '@/modules/upload'
import { cn } from '@/utils'
import { FileItem } from './FileItem'
import { formatDropzoneHint, formatTypeRestriction } from './hint'
import { PageDropOverlay } from './PageDropOverlay'
import { refuseFilesOverPageLimit } from './pageLimit'
import { hasInvalidType } from './rejection'
import { usePageDrag } from './usePageDrag'

interface FileDropzoneProps {
  files: FileDropzoneItem[]
  errorsByFile: (Error | null)[]
  setFiles: React.Dispatch<React.SetStateAction<FileDropzoneItem[]>>
  accept?: Accept
  disabled?: boolean
  maxSize?: number
  maxPages?: number
  collapsed?: boolean
  pageWideDrop?: boolean
}

export const FileDropzone = ({
  files,
  errorsByFile,
  setFiles,
  accept,
  maxSize,
  maxPages,
  disabled,
  collapsed = false,
  pageWideDrop = false,
}: FileDropzoneProps) => {
  const onDrop = useCallback(
    async (
      acceptedFiles: File[],
      rejectedFiles: FileRejection[],
      event: DropEvent | null,
    ) => {
      // A drag that showed the red reject state must add nothing. Picked files still list their rejections.
      const isDragDrop = event?.type === 'drop'
      if (isDragDrop && hasInvalidType(rejectedFiles)) return
      const checkedFiles =
        maxPages === undefined
          ? acceptedFiles
          : // react-dropzone hands over FileWithPath objects; its onDrop types them as plain File.
            await refuseFilesOverPageLimit(
              acceptedFiles as FileWithPath[],
              maxPages,
            )
      setFiles((prev) => [...prev, ...rejectedFiles, ...checkedFiles])
    },
    [setFiles, maxPages],
  )

  const mbInBytes = 1000000
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept,
      maxSize: maxSize ? maxSize * mbInBytes : undefined,
      disabled,
    })

  const removeFile = (i: number) => {
    const value = [...files]
    value.splice(i, 1)
    setFiles(value)
  }
  const hasFiles = files.length > 0
  const hint = formatDropzoneHint(accept, maxSize, maxPages)
  const typeRestriction = formatTypeRestriction(accept)
  const DropIcon = isDragReject ? FileXIcon : CloudUploadIcon
  const isStrip = collapsed && !hasFiles
  const isPageDragging = usePageDrag(pageWideDrop && !disabled)

  const uploadLink = (
    <Link
      theme="brand"
      className={cn('underline', disabled && 'opacity-50 cursor-not-allowed')}
      asChild
    >
      <button type="button" disabled={disabled}>
        Click to upload
      </button>
    </Link>
  )

  // The dropzone has one root. During a page-wide drag it is the overlay, so the drop lands
  // wherever the pointer lets go and still goes through the same type and size checks.
  const rootProps = getRootProps()
  const overlayRootProps = isPageDragging ? rootProps : {}
  const boxRootProps = isPageDragging ? {} : rootProps

  return (
    <div className="flex flex-col gap-2">
      {isPageDragging && (
        <PageDropOverlay
          rootProps={overlayRootProps}
          hint={hint}
          rejectMessage={isDragReject ? typeRestriction : undefined}
        />
      )}

      <div {...boxRootProps}>
        {isStrip ? (
          <div
            className={cn(
              'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-dashed border-zinc-950/20 px-5 py-4',
              isDragActive && 'bg-brand/10',
              isDragReject && 'border-red-600 bg-red-600/10',
            )}
          >
            <input {...getInputProps()} />
            <DropIcon
              className={cn(
                'size-6 shrink-0',
                isDragReject ? 'text-red-600' : 'text-brand',
              )}
            />
            {isDragReject ? (
              <p className="font-medium text-red-600" role="alert">
                {typeRestriction}
              </p>
            ) : (
              <p className="font-medium">
                Drop files here or {uploadLink} to add more
              </p>
            )}
            {hint && <p className="text-ink-subtle ml-auto text-xs">{hint}</p>}
          </div>
        ) : (
          <div
            className={cn(
              'flex flex-col gap-1 items-center rounded-md border border-dashed border-zinc-950/20 p-10',
              isDragActive && 'bg-brand/10',
              isDragReject && 'border-red-600 bg-red-600/10',
              hasFiles && 'p-4',
            )}
          >
            <input {...getInputProps()} />
            <DropIcon
              className={cn(
                'size-10',
                isDragReject ? 'text-red-600' : 'text-brand',
              )}
            />
            {isDragReject ? (
              <p className="text-red-600" role="alert">
                {typeRestriction}
              </p>
            ) : (
              <p>{uploadLink} or drag and drop</p>
            )}
            {hint && <p className="text-ink-subtle text-xs">{hint}</p>}
          </div>
        )}
      </div>

      {hasFiles && (
        <div
          data-slot="control"
          className={cn(
            // Basic layout
            'group relative block w-full',
            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            'before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow',
            // Focus ring
            'after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:has-[[data-focus]]:ring-2 after:has-[[data-focus]]:ring-blue-500',
            // Disabled state
            'has-[[data-disabled]]:opacity-50 before:has-[[data-disabled]]:bg-zinc-950/5 before:has-[[data-disabled]]:shadow-none',
          )}
        >
          <div
            className={cn(
              'relative block w-full appearance-none rounded-md p-2',
              // Border
              'border border-zinc-950/10 data-[hover]:border-zinc-950/20',
              // Background color
              'bg-transparent',
              'max-h-96 overflow-auto',
            )}
          >
            {files.map((item, i) => (
              <FileItem
                key={`file-item-${i}`}
                item={item}
                hasError={!!errorsByFile[i]}
                accept={accept}
                maxSize={maxSize}
                onRemove={() => removeFile(i)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
